import { Component } from '@theme/component';

/**
 * HH · Reseñas (portada): una reseña a la vez, con cambio automático.
 *
 * - Botones anterior y siguiente, miniaturas y flechas del teclado dentro del carrusel.
 * - En el celular, deslizar el dedo a los lados cambia de reseña.
 * - Cambio automático cada `data-interval` ms (si tiene `data-autoplay`). Se detiene mientras el
 *   mouse está encima, mientras el foco está dentro, fuera de la pantalla y con la pestaña en
 *   segundo plano; el botón de pausa lo detiene hasta que se vuelva a tocar. Con «reducir
 *   movimiento» empieza en pausa.
 * - Las fotos de las reseñas que todavía no se ven llegan en `data-src`: se piden al acercarse
 *   su turno (la actual y la siguiente), no al cargar la página.
 * - Mientras cambia sola, el lector de pantalla no anuncia cada reseña (aria-live="off"); al
 *   cambiarla a mano, sí.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} viewport
 * @property {HTMLButtonElement[]} [thumbs]
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [counter]
 * @property {HTMLElement} [progress]
 *
 * @extends {Component<Refs>}
 */
export class HhResenas extends Component {
  requiredRefs = ['slides', 'viewport'];

  #index = 0;
  #timer = 0;
  /** Pausado con el botón (o por «reducir movimiento»). */
  #userPaused = false;
  #hovered = false;
  #focused = false;
  #onScreen = false;
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;

  get #autoplay() {
    return this.hasAttribute('data-autoplay') && this.refs.slides.length > 1;
  }

  get #interval() {
    const value = Number(this.dataset.interval);
    return Number.isFinite(value) && value >= 2000 ? value : 7000;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setUp();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#tearDown();
    this.#setUp();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tearDown();
  }

  /** Reseña anterior. */
  previous() {
    this.#go(this.#index - 1, true);
  }

  /** Reseña siguiente. */
  next() {
    this.#go(this.#index + 1, true);
  }

  /**
   * Miniatura tocada.
   * @param {Event} event
   */
  select(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-index]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#go(Number(button.dataset.index), true);
  }

  /** Pausa o reanuda el cambio automático. */
  toggle() {
    this.#userPaused = !this.#userPaused;
    this.#syncToggle();
    this.#syncLive();
    this.#schedule();
  }

  /**
   * Flechas izquierda y derecha dentro del carrusel.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== isRtl;
    event.preventDefault();
    this.#go(this.#index + (forward ? 1 : -1), true);
  }

  #setUp() {
    const { slides, viewport } = this.refs;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const active = slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.#index = active === -1 ? 0 : active;
    this.#userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse') this.#setHovered(true);
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'mouse') this.#setHovered(false);
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      (event) => {
        // Solo el foco del teclado detiene el cambio: un clic en «siguiente» no lo deja en pausa.
        if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#setFocused(true);
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#setFocused(false);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        this.#pointerStart = { x: event.clientX, y: event.clientY };
      },
      { signal }
    );
    viewport.addEventListener('pointerup', (event) => this.#onSwipeEnd(event), { signal });
    viewport.addEventListener(
      'pointercancel',
      () => {
        this.#pointerStart = null;
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#onScreen = entry.isIntersecting;
        this.#schedule();
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);

    this.#loadImages(this.#index);
    this.#syncToggle();
    this.#syncLive();
  }

  #tearDown() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }

  /** @param {boolean} value */
  #setHovered(value) {
    this.#hovered = value;
    this.#schedule();
  }

  /** @param {boolean} value */
  #setFocused(value) {
    this.#focused = value;
    this.#syncLive();
    this.#schedule();
  }

  /** @param {PointerEvent} event */
  #onSwipeEnd(event) {
    const start = this.#pointerStart;
    this.#pointerStart = null;
    if (!start) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = deltaX < 0 !== isRtl;
    this.#go(this.#index + (forward ? 1 : -1), true);
  }

  /**
   * @param {number} target
   * @param {boolean} byUser
   */
  #go(target, byUser) {
    const { slides, thumbs, counter } = this.refs;
    const count = slides.length;
    if (count < 2) return;

    const index = ((target % count) + count) % count;
    this.#syncLive(byUser);

    if (index !== this.#index) {
      slides.forEach((slide, position) => {
        const active = position === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
      });

      thumbs?.forEach((thumb, position) => {
        if (position === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      this.#index = index;
      this.#loadImages(index);
    }

    this.#schedule();
  }

  /**
   * Pide la foto de la reseña que se muestra y la de la siguiente.
   * @param {number} index
   */
  #loadImages(index) {
    const { slides } = this.refs;
    for (const position of [index, (index + 1) % slides.length]) {
      for (const image of slides[position]?.querySelectorAll('img[data-src]') ?? []) {
        if (!(image instanceof HTMLImageElement)) continue;
        image.src = image.dataset.src ?? '';
        image.removeAttribute('data-src');
      }
    }
  }

  get #playing() {
    return (
      this.#autoplay &&
      !this.#userPaused &&
      !this.#hovered &&
      !this.#focused &&
      this.#onScreen &&
      document.visibilityState === 'visible'
    );
  }

  /** Programa el siguiente cambio y reinicia la barra de progreso. */
  #schedule() {
    window.clearTimeout(this.#timer);
    this.#timer = 0;

    const { progress } = this.refs;
    progress?.classList.remove('is-running');

    if (!this.#playing) return;

    if (progress) {
      // Reinicia la animación de la barra desde cero.
      void progress.offsetWidth;
      progress.classList.add('is-running');
    }

    this.#timer = window.setTimeout(() => this.#go(this.#index + 1, false), this.#interval);
  }

  #syncToggle() {
    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.toggleAttribute('data-paused', this.#userPaused);
    const label = this.#userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /**
   * Anuncia los cambios solo cuando no está cambiando sola.
   * @param {boolean} [force]
   */
  #syncLive(force = false) {
    const { viewport } = this.refs;
    const polite = force || !this.#autoplay || this.#userPaused || this.#focused;
    viewport.setAttribute('aria-live', polite ? 'polite' : 'off');
  }
}

if (!customElements.get('hh-resenas')) {
  customElements.define('hh-resenas', HhResenas);
}
